/* External */
import {
  useCallback, useEffect, useRef, memo, useState,
  forwardRef, useImperativeHandle,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { toZonedTime } from 'date-fns-tz';
import { isAfter, isEqual } from 'date-fns';

/* Ott-common */
import { getPlaybackInfoWithAuthorizerV3, Errors } from '@dtvgo/player-utils';
import { PROVIDER } from '@dtvgo/backend-service/normalize';

/* Hooks */
import backendService from '~/services/backendService';
import useRefSync from '~/hooks/useRefSync';
import { usePlayerBuffer } from '~/providers/ConfigProvider/ConfigProvider';

/* Utils */
import { noOp, appConsoleLog } from '~/utils/functions';
import platform from '~/utils/platform';
import {
  IS_TAPPP_SDK_ACTIVE,
  USE_LOW_LATENCY_STREAMS,
  isProgramCompletionActive,
} from '~/utils/env';

import { checkToken } from '~/services/authService';
import Player from '../Player';
import usePlaybackHeartbeatController from './utils/usePlaybackHeartbeatController';

function VrioPlayer({
  // Own props
  className = '',
  playbackId,
  channelId = undefined,
  schedule = undefined,
  content = undefined,
  savedProgress = undefined,
  isContentRequestCompleted = true,
  channelName = '',
  isFromInterstitial = false,
  homeOnBack = false,
  isCurrentInterstitialSchedule = undefined,
  isLive = false,
  hasNextEpisode = false,
  playSource = '',
  onEnded = noOp,
  onProgress = noOp,
  onPause = noOp,
  onPlaying = noOp,
  onSeeked = noOp,
  onError = noOp,
  onAuthorizerError = noOp,
  goToNextEpisode = undefined,
  onUserInteraction = noOp,
  counterEpisode = 0,
  childrenWrapper = undefined,
  setPlayerReadyToPlay = undefined,
  onAuthorizerResponseReceived = noOp,
  onGoLive = undefined,
  children = undefined,
}, ref) {
  const [sources, setSources] = useState(undefined);
  const [hasPlaybackAuthorizerSuccess, setHasPlaybackAuthorizerSuccess] = useState(false);
  const clearRefreshTimeoutRef = useRef(undefined);
  const {
    streamedTimeCounter,
    streamedContentTimeCounter,
    playbackHeartbeatController,
  } = usePlaybackHeartbeatController();
  const contentRef = useRefSync(content);
  const onAuthorizerErrorRef = useRefSync(onAuthorizerError);
  const onAuthorizerResponseReceivedRef = useRefSync(onAuthorizerResponseReceived);

  const handleAuthorizerError = useCallback((error) => {
    onAuthorizerErrorRef.current?.(Errors.parseAuthorizerError(error));
  }, [onAuthorizerErrorRef]);

  const isPlaying = useRef(false);
  const bufferConfig = usePlayerBuffer(isLive, PROVIDER.VRIO);

  const handlePlaybackStopped = useCallback(async () => {
    isPlaying.current = false;
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
    if (clearRefreshTimeoutRef.current) clearRefreshTimeoutRef.current();
    await playbackHeartbeatController.stop();
    streamedTimeCounter.reset();
    streamedContentTimeCounter.reset();
  }, [playbackHeartbeatController, streamedTimeCounter, streamedContentTimeCounter]);

  const handleLicenseFailed = useCallback((error) => {
    handlePlaybackStopped();
    onError(Errors.parsePlayerError(error));
  }, [handlePlaybackStopped, onError]);

  const handleError = useCallback(
    (error) => {
      handlePlaybackStopped();
      onError(Errors.parsePlayerError(error));
    },
    [handlePlaybackStopped, onError],
  );

  const handlePlaying = useCallback(() => {
    streamedTimeCounter.start();
    streamedContentTimeCounter.start();
    isPlaying.current = true;
    onPlaying();
  }, [onPlaying, streamedTimeCounter, streamedContentTimeCounter]);

  const handlePause = useCallback(() => {
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
    isPlaying.current = false;
    onPause();
  }, [onPause, streamedTimeCounter, streamedContentTimeCounter]);

  const handleProgress = useCallback((progress) => {
    playbackHeartbeatController.updateProgress(progress);
    onProgress(progress);
  }, [playbackHeartbeatController, onProgress]);

  const handleDurationChange = useCallback((duration) => {
    playbackHeartbeatController.updateDuration(duration);
  }, [playbackHeartbeatController]);

  const handleBuffering = useCallback(() => {
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
  }, [streamedTimeCounter, streamedContentTimeCounter]);

  const handleBufferingEnded = useCallback(() => {
    if (isPlaying.current) {
      streamedTimeCounter.start();
      streamedContentTimeCounter.start();
    }
  }, [streamedTimeCounter, streamedContentTimeCounter]);

  const handleEnded = useCallback(() => {
    handlePlaybackStopped();
    onEnded();
  }, [handlePlaybackStopped, onEnded]);

  const goToNextEpisodeCallBack = useCallback(() => {
    handlePlaybackStopped();
    goToNextEpisode();
  }, [goToNextEpisode, handlePlaybackStopped]);

  const playerRef = useRef();
  const stop = useCallback(() => {
    playerRef.current?.unload();
    handlePlaybackStopped();
  }, [handlePlaybackStopped]);

  useImperativeHandle(ref, () => ({ stop }), [stop]);

  const getAssetAndScheduleForHeartbeat = useCallback(() => {
    if (!contentRef.current) {
      return { asset: undefined, schedule: undefined };
    }
    return {
      asset: {
        vmsId: contentRef.current?.vmsId,
        vrioAssetId: contentRef.current?.vrioAssetId,
        duration: contentRef.current?.duration,
      },
      schedule: {
        guideId: contentRef.current?.guideId,
        startTime: contentRef.current?.live?.startTime
          && new Date(contentRef.current?.live?.startTime),
        endTime: contentRef.current?.live?.endTime
          && new Date(contentRef.current?.live?.endTime),
      },
    };
  }, [contentRef]);

  // When playbackId changes, initialize new playback
  useEffect(() => {
    async function initNewPlayback() {
      try {
        await handlePlaybackStopped();
        const {
          sources: newSources,
          clearRefreshTimeout,
          ...authorizerResponse
        } = await getPlaybackInfoWithAuthorizerV3(
          backendService,
          {
            isLive,
            contentId: playbackId,
          },
          {
            onAuthorizerError: handleAuthorizerError,
          },
          checkToken,
        );
        clearRefreshTimeoutRef.current = clearRefreshTimeout;
        setSources(
          USE_LOW_LATENCY_STREAMS
            ? newSources
            : newSources?.filter(({ isLowLatency }) => !isLowLatency),
        );
        setHasPlaybackAuthorizerSuccess(true);
        onAuthorizerResponseReceivedRef.current?.(authorizerResponse);
        playbackHeartbeatController.start({
          playbackId,
          isLive,
          ...getAssetAndScheduleForHeartbeat(),
        });
      } catch (error) {
        setHasPlaybackAuthorizerSuccess(false);
        handleAuthorizerError(error);
      }
    }

    if (playbackId) {
      initNewPlayback();
    }
  }, [
    playbackId,
    isLive,
    handleAuthorizerError,
    handlePlaybackStopped,
    getAssetAndScheduleForHeartbeat,
    playbackHeartbeatController,
    onAuthorizerResponseReceivedRef,
    contentRef,
  ]);

  // When schedule changed, we need to reset streamed time and update content for heartbeat
  useEffect(() => {
    streamedTimeCounter.reset();
    streamedContentTimeCounter.reset();
    playbackHeartbeatController.setContent(getAssetAndScheduleForHeartbeat());
    if (isPlaying.current) {
      streamedTimeCounter.start();
      streamedContentTimeCounter.start();
    }
  }, [
    schedule?.guideId,
    streamedTimeCounter,
    playbackHeartbeatController,
    streamedContentTimeCounter,
    getAssetAndScheduleForHeartbeat,
  ]);

  // When this component is unmounted, we need to stop playback
  useEffect(() => () => {
    handlePlaybackStopped();
  }, [handlePlaybackStopped]);

  // Update progress to send in heartbeat when saved progress is available
  useEffect(() => {
    if (savedProgress !== undefined) {
      playbackHeartbeatController.updateProgress(savedProgress);
    }
  }, [playbackHeartbeatController, savedProgress]);

  // This removal is necessary for the SDK to work correctly when closing the player - 05/15/2025
  useEffect(() => () => {
    if (isLive && IS_TAPPP_SDK_ACTIVE && window.TapppPanelLibrary) {
      try {
        window.TapppPanelLibrary?.destroy();

        appConsoleLog('[TAPPP] VrioPlay: Called Destroy TapppPanel');
      } catch (error) {
        console.error('[TAPPP] Error on VrioPlay call destroy TapppPanel', error);
      }
    }
  }, [isLive]);

  const currentContentEndTimeUtc = useMemo(() => {
    if (!content?.live?.endTime) return undefined;
    const parsedDate = new Date(content?.live?.endTime).getTime();

    return toZonedTime(parsedDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, [content?.live?.endTime]);

  const prevContentEndTimeUtcRef = useRef(null);
  const prevContentGuideIdRef = useRef(null);

  const programCompletion = useCallback(async () => {
    await playbackHeartbeatController.programCompletion();
  }, [playbackHeartbeatController]);

  /**
  * When a live program is changed and the user continues watching on the same
  * channel, we send the learn action programCompletion.
  */
  useEffect(() => {
    if (isLive && isProgramCompletionActive) {
      const currentTime = new Date();
      const currentTimeUtc = toZonedTime(
        currentTime,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );

      const hasProgramChanged = content?.guideId !== prevContentGuideIdRef.current;

      if (hasProgramChanged && prevContentEndTimeUtcRef.current) {
        const prevContentEndTime = prevContentEndTimeUtcRef.current;
        const isPrevContentFinished = isAfter(currentTimeUtc, prevContentEndTime)
        || isEqual(currentTimeUtc, prevContentEndTime);

        if (isPrevContentFinished) {
          programCompletion();
          streamedContentTimeCounter.reset();
        }
      }

      if (content) {
        prevContentGuideIdRef.current = content.guideId;
        prevContentEndTimeUtcRef.current = currentContentEndTimeUtc;
        streamedContentTimeCounter.start();
      } else {
        prevContentGuideIdRef.current = undefined;
        prevContentEndTimeUtcRef.current = undefined;
      }
    }
  }, [
    isLive,
    content,
    content?.guideId,
    programCompletion,
    currentContentEndTimeUtc,
    streamedContentTimeCounter,
  ]);

  return (
    <Player
      className={className}
      bufferConfig={bufferConfig}
      isLive={isLive}
      sources={(isLive || savedProgress !== undefined) ? sources : undefined}
      drmKeySystem={platform.drmKeySystem}
      savedProgress={savedProgress}
      hasNextEpisode={hasNextEpisode}
      schedule={schedule}
      content={content}
      isContentRequestCompleted={isContentRequestCompleted}
      channelId={channelId}
      channelName={channelName}
      playSource={playSource}
      goToNextEpisode={goToNextEpisodeCallBack}
      shouldReloadOnError={Errors.isRecoverableByReloading}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onProgress={handleProgress}
      onSeeked={onSeeked}
      onBuffering={handleBuffering}
      onBufferingEnded={handleBufferingEnded}
      onEnded={handleEnded}
      onLicenseFailed={handleLicenseFailed}
      onError={handleError}
      isFromInterstitial={isFromInterstitial}
      homeOnBack={homeOnBack}
      isCurrentInterstitialSchedule={isCurrentInterstitialSchedule}
      onUserInteraction={onUserInteraction}
      counterEpisode={counterEpisode}
      onDurationChange={handleDurationChange}
      childrenWrapper={childrenWrapper}
      setPlayerReadyToPlay={setPlayerReadyToPlay}
      hasPlaybackAuthorizerSuccess={hasPlaybackAuthorizerSuccess}
      onGoLive={onGoLive}
      ref={playerRef}
    >
      {children}
    </Player>
  );
}

const VrioPlayerWithRef = forwardRef(VrioPlayer);

VrioPlayer.propTypes = {
  // Own props
  className: PropTypes.string,
  playbackId: PropTypes.string.isRequired,
  channelId: PropTypes.string,
  schedule: PropTypes.shape({}),
  content: PropTypes.shape({}),
  savedProgress: PropTypes.number,
  isContentRequestCompleted: PropTypes.bool,
  channelName: PropTypes.string,
  isLive: PropTypes.bool,
  hasNextEpisode: PropTypes.bool,
  playSource: PropTypes.string,
  onEnded: PropTypes.func,
  onProgress: PropTypes.func,
  onPause: PropTypes.func,
  onPlaying: PropTypes.func,
  onSeeked: PropTypes.func,
  onError: PropTypes.func,
  onAuthorizerError: PropTypes.func,
  goToNextEpisode: PropTypes.func,
  isFromInterstitial: PropTypes.bool,
  homeOnBack: PropTypes.bool,
  isCurrentInterstitialSchedule: PropTypes.bool,
  onUserInteraction: PropTypes.func,
  counterEpisode: PropTypes.number,
  npawError: PropTypes.shape({}),

  childrenWrapper: PropTypes.func,
  setPlayerReadyToPlay: PropTypes.func,
  onAuthorizerResponseReceived: PropTypes.func,
  onGoLive: PropTypes.func,
  children: PropTypes.node,
};

export default memo(VrioPlayerWithRef);
